import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CMSSection {
  id: string;
  section_key: string;
  title: string | null;
  content: any;
  updated_at: string;
}

/**
 * Fetch all CMS content sections from the database.
 * staleTime is 0 so the frontend always shows fresh content.
 */
export function useCMSContent() {
  return useQuery<CMSSection[]>({
    queryKey: ["cms-content"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cms_content")
        .select("*")
        .order("section_key");
      if (error) throw error;
      return data ?? [];
    },
    /* CMS changes rarely — cache for 2 min, realtime invalidation handles updates */
    staleTime: 2 * 60_000,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/** Helper: get a specific section's content by key */
export function useCMSSection(key: string) {
  const { data: sections = [], ...rest } = useCMSContent();
  const section = sections.find((s) => s.section_key === key);
  return { section, content: section?.content ?? null, ...rest };
}

/** Subscribe to realtime CMS changes — call once at app level */
export function useCMSRealtime() {
  const qc = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel("cms-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cms_content" },
        () => {
          qc.invalidateQueries({ queryKey: ["cms-content"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [qc]);
}
